import type {
  ParsedCustomizationOptions,
  GoldColorOption,
  GoldPurityOption,
  DiamondOption,
  SolitaireCaratOption,
  SolitaireNameOption,
  SolitaireQualityOption,
} from '../domain/models/CustomizationOptions';
import { parseSizeList } from './parseSizeList';

interface MetalRow {
  color?: string;
  purityAndFullName?: Array<{ metalPurity?: string }>;
}

function mapApiColorToMetalColor(color: string): { color: string; displayName: string } {
  if (color === 'Yellow') return { color: 'Gold', displayName: 'Yellow' };
  if (color === 'White') return { color: 'White Gold', displayName: 'White' };
  if (color === 'Rose') return { color: 'Rose Gold', displayName: 'Rose' };
  if (color === 'Gold') return { color: 'Gold', displayName: 'Yellow' };
  if (color === 'White Gold') return { color: 'White Gold', displayName: 'White' };
  if (color === 'Rose Gold') return { color: 'Rose Gold', displayName: 'Rose' };
  return { color, displayName: color };
}

function metalColorMatchesRow(metalColor: string, rowColor: string): boolean {
  const normalized = mapApiColorToMetalColor(rowColor);
  return normalized.color === metalColor || rowColor.includes(metalColor.replace(' Gold', ''));
}

function extractMetalRaw(source: unknown): unknown {
  if (!source || typeof source !== 'object') return null;
  const record = source as Record<string, unknown>;
  if ('metal' in record || 'solitaire' in record || 'diamond' in record) {
    return record.metal ?? null;
  }
  return source;
}

function parseGoldColors(metalRaw: unknown): GoldColorOption[] {
  if (!metalRaw) return [];

  const colors: GoldColorOption[] = [];
  const seen = new Set<string>();

  const addColor = (rawColor: string) => {
    const mapped = mapApiColorToMetalColor(rawColor);
    if (seen.has(mapped.color)) return;
    seen.add(mapped.color);
    colors.push(mapped);
  };

  if (Array.isArray(metalRaw)) {
    for (const row of metalRaw) {
      if (row && typeof row === 'object' && typeof (row as MetalRow).color === 'string') {
        addColor((row as MetalRow).color!);
      }
    }
  } else if (typeof metalRaw === 'object') {
    for (const key of Object.keys(metalRaw as Record<string, unknown>)) {
      const row = (metalRaw as Record<string, MetalRow>)[key];
      if (row?.color) addColor(row.color);
    }
  }

  return colors;
}

export function parseGoldPuritiesForMetal(source: unknown, activeMetalColor: string): GoldPurityOption[] {
  const metalRaw = extractMetalRaw(source);
  if (!metalRaw || !activeMetalColor) return [];

  const purities: GoldPurityOption[] = [];
  const seen = new Set<string>();

  const addPurities = (row: MetalRow) => {
    if (!row.purityAndFullName || !Array.isArray(row.purityAndFullName)) return;
    if (!row.color || !metalColorMatchesRow(activeMetalColor, row.color)) return;
    for (const purity of row.purityAndFullName) {
      const metalPurity = purity.metalPurity;
      if (!metalPurity || seen.has(metalPurity)) continue;
      seen.add(metalPurity);
      purities.push({ metalPurity });
    }
  };

  if (Array.isArray(metalRaw)) {
    for (const row of metalRaw) {
      if (row && typeof row === 'object') addPurities(row as MetalRow);
    }
  } else if (typeof metalRaw === 'object') {
    for (const key of Object.keys(metalRaw as Record<string, unknown>)) {
      addPurities((metalRaw as Record<string, MetalRow>)[key]);
    }
  }

  return purities;
}

function parseDiamonds(diamondRaw: unknown): DiamondOption[] {
  if (!Array.isArray(diamondRaw)) return [];
  return diamondRaw.map((item) => {
    const row = item as { clarity?: string; color?: string };
    const clarity = row.clarity ?? '';
    const color = row.color ?? '';
    const value = `${clarity}${color}`;
    return {
      clarity,
      color,
      value,
      label: `${clarity} ${color}`.trim(),
    };
  });
}

function parseSolitaireCarats(raw: unknown): SolitaireCaratOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const row = item as { solitaireTotalWeight?: number | string };
    const weight = parseFloat(String(row.solitaireTotalWeight ?? 0));
    return { weight };
  });
}

function parseSolitaireNames(raw: unknown): SolitaireNameOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const row = item as { name?: string };
    return { name: row.name ?? '' };
  }).filter((item) => item.name !== '');
}

function parseSolitaireQualities(raw: unknown): SolitaireQualityOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const row = item as { clarity?: string; color?: string };
    const clarity = row.clarity ?? '';
    const color = row.color ?? '';
    const value = `${clarity}_${color}`;
    return {
      clarity,
      color,
      value,
      label: `${clarity}${color}`,
    };
  });
}

export function parseCustomizationOptions(customizationOptions: unknown): ParsedCustomizationOptions {
  const empty: ParsedCustomizationOptions = {
    isSolitaire: false,
    goldColors: [],
    goldPurities: [],
    diamonds: [],
    solitaireCarats: [],
    solitaireClarities: [],
    solitaireColors: [],
    solitaireQualities: [],
    sizes: parseSizeList(customizationOptions),
  };

  if (!customizationOptions || typeof customizationOptions !== 'object') return empty;

  const opts = customizationOptions as Record<string, unknown>;
  const solitaireRaw = opts.solitaire;
  const isSolitaire = solitaireRaw != null && typeof solitaireRaw === 'object';

  const solitaire = isSolitaire ? (solitaireRaw as Record<string, unknown>) : null;

  return {
    isSolitaire,
    goldColors: parseGoldColors(opts.metal),
    goldPurities: [],
    diamonds: isSolitaire ? [] : parseDiamonds(opts.diamond),
    solitaireCarats: solitaire ? parseSolitaireCarats(solitaire.carat) : [],
    solitaireClarities: solitaire ? parseSolitaireNames(solitaire.clarity) : [],
    solitaireColors: solitaire ? parseSolitaireNames(solitaire.color) : [],
    solitaireQualities: solitaire ? parseSolitaireQualities(solitaire.quality) : [],
    sizes: parseSizeList(customizationOptions),
  };
}

export function normalizeMetalColorForApi(metal: string): string {
  if (metal === 'Gold') return 'Gold';
  if (metal === 'Yellow Gold') return 'Gold';
  return metal;
}

export function metalToSelectionColor(metal: string): string {
  if (metal === 'Gold') return 'Gold';
  return metal;
}

export function resolveActiveMetalColor(metal: string): string {
  if (metal === 'Gold') return 'Gold';
  if (metal === 'Yellow Gold') return 'Gold';
  return metal;
}

export function hasCustomizationOptions(customizationOptions: unknown): boolean {
  const parsed = parseCustomizationOptions(customizationOptions);
  return (
    parsed.goldColors.length > 0 ||
    parsed.diamonds.length > 0 ||
    parsed.solitaireCarats.length > 0 ||
    parsed.solitaireClarities.length > 0 ||
    parsed.solitaireColors.length > 0 ||
    parsed.solitaireQualities.length > 0
  );
}
